import { useEffect, useState, type FormEvent } from "react";
import { supabase, functionErrorMessage } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { IntakeFieldListEditor } from "../lib/IntakeFieldListEditor.js";
import { INDUSTRY_INTAKE_TEMPLATES, type IntakeFieldDef } from "../lib/intakeFields.js";
import { Button, Field, Input, Modal } from "../lib/ui.js";
import { formatServicePrice } from "../lib/money.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { StaffHours } from "./StaffHours.js";
import { useJobsEnabled } from "../lib/jobs.js";

interface ServiceRow {
  id: string;
  name: string;
  /** Groups services on the booking page; null = none. */
  category: string | null;
  duration_minutes: number;
  price_cents: number | null;
  /** The price is a starting price ("from $45"). */
  price_from: boolean;
  active: boolean;
  intake_fields: IntakeFieldDef[];
  /** Booking this service makes a job (Leads and jobs, migration 0086): the Inspection. */
  creates_job: boolean;
}

interface ResourceTypeRow {
  id: string;
  name: string;
}

interface RuleRow {
  service_id: string;
  resource_id: string | null;
  resource_type_id: string | null;
}

interface ResourceRow {
  id: string;
  name: string;
  active: boolean;
  resource_type_id: string | null;
  resource_types: { name: string } | null;
}

interface StaffLogin {
  resource_id: string;
  user_id: string;
  email: string | null;
  phone: string | null;
  role: "owner" | "manager" | "staff";
}

/**
 * A staff member's own login (role "staff", migration 0060): it sees and manages only this person's bookings, working hours and
 * time off. Added by email (a set-password invite) or by mobile number (they sign in with a texted code).
 */
function StaffLoginCell({ businessId, resource, login, onChanged }: { businessId: string; resource: ResourceRow; login: StaffLogin | undefined; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const [by, setBy] = useState<"email" | "mobile">("email");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    const { error: err } = await supabase.functions.invoke("invite-member", { body: { business_id: businessId, [by]: contact, role: "staff", resource_id: resource.id } });
    setBusy(false);
    if (err) {
      setMessage(await functionErrorMessage(err));
      return;
    }
    setAdding(false);
    setContact("");
    onChanged();
  }

  async function remove() {
    if (!window.confirm(`Remove the login for ${resource.name}? They will no longer be able to sign in to this business.`)) return;
    const { error: err } = await supabase.rpc("rpc_portal_unlink_staff_login", { p_business_id: businessId, p_resource_id: resource.id });
    if (err) {
      setMessage(err.message);
      return;
    }
    onChanged();
  }

  if (login) {
    const who = login.email ?? (login.phone ? formatAuPhoneForDisplay(login.phone.startsWith("+") ? login.phone : `+${login.phone}`) : "-");
    return (
      <>
        {who}{" "}
        <button onClick={() => void remove()} style={{ ...linkButtonStyle, color: "var(--rc-danger)", marginLeft: 6 }}>
          Remove login
        </button>
        {login.role !== "staff" && <div style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>{login.role === "owner" ? "Owner" : "Manager"}: sees the whole business</div>}
        {message && <div style={{ color: "var(--rc-danger)", fontSize: 12.5 }}>{message}</div>}
      </>
    );
  }
  if (!adding) {
    return (
      <>
        <button onClick={() => setAdding(true)} style={linkButtonStyle}>
          Add login
        </button>
        {message && <div style={{ color: "var(--rc-danger)", fontSize: 12.5 }}>{message}</div>}
      </>
    );
  }
  return (
    <form onSubmit={invite} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <select value={by} onChange={(e) => setBy(e.target.value as "email" | "mobile")} style={{ padding: 4 }}>
        <option value="email">Email</option>
        <option value="mobile">Mobile</option>
      </select>
      <input
        required
        autoFocus
        type={by === "email" ? "email" : "tel"}
        placeholder={by === "email" ? "name@example.com" : undefined}
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        style={{ padding: 4, width: 170 }}
      />
      <button type="submit" disabled={busy} style={linkButtonStyle}>
        {busy ? "Adding..." : "Add"}
      </button>
      <button type="button" onClick={() => setAdding(false)} style={linkButtonStyle}>
        Cancel
      </button>
      {message && <div style={{ color: "var(--rc-danger)", fontSize: 12.5, width: "100%" }}>{message}</div>}
    </form>
  );
}

export function Services() {
  const { activeBusinessId } = useAuth();
  const readOnly = useReadOnly();
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeRow[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [logins, setLogins] = useState<Map<string, StaffLogin>>(new Map());
  const [industry, setIndustry] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** A warning that must be seen (shown in a pop-up): a staff name too close to the receptionist's. */
  const [warning, setWarning] = useState<string | null>(null);

  const [svcName, setSvcName] = useState("");
  const [svcCategory, setSvcCategory] = useState("");
  const [svcDuration, setSvcDuration] = useState("30");
  const [svcPrice, setSvcPrice] = useState("");
  const [svcPriceFrom, setSvcPriceFrom] = useState(false);
  const [svcFields, setSvcFields] = useState<IntakeFieldDef[]>([]);

  // null = "every active staff member" (the default for a new service).
  const [svcStaff, setSvcStaff] = useState<Set<string> | null>(null);

  const [editingService, setEditingService] = useState<ServiceRow | null>(null);

  const [resName, setResName] = useState("");
  const [resTypeId, setResTypeId] = useState("");
  const [newTypeName, setNewTypeName] = useState("");

  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
  const [editResName, setEditResName] = useState("");
  const [editResTypeId, setEditResTypeId] = useState("");

  const categories = [...new Set(services.map((s) => s.category).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b));
  const industryTemplate = industry ? INDUSTRY_INTAKE_TEMPLATES[industry] : undefined;

  async function load() {
    if (!activeBusinessId) return;
    setLoading(true);
    const [{ data: business }, { data: svc }, { data: types }, { data: res }, { data: ruleRows }, { data: loginRows }] = await Promise.all([
      supabase.from("businesses").select("industry").eq("id", activeBusinessId).single(),
      supabase
        .from("services")
        .select("id, name, category, duration_minutes, price_cents, price_from, active, intake_fields, creates_job")
        .eq("business_id", activeBusinessId)
        .order("category", { nullsFirst: false })
        .order("name"),
      supabase.from("resource_types").select("id, name").eq("business_id", activeBusinessId).order("name"),
      supabase
        .from("resources")
        .select("id, name, active, resource_type_id, resource_types(name)")
        .eq("business_id", activeBusinessId)
        .order("name"),
      supabase
        .from("service_resource_rules")
        .select("service_id, resource_id, resource_type_id")
        .eq("business_id", activeBusinessId),
      supabase.rpc("rpc_portal_staff_logins", { p_business_id: activeBusinessId }),
    ]);
    setLogins(new Map(((loginRows ?? []) as StaffLogin[]).map((l) => [l.resource_id, l])));
    setRules((ruleRows ?? []) as RuleRow[]);
    setIndustry(business?.industry ?? null);
    setServices((svc ?? []) as ServiceRow[]);
    setResourceTypes((types ?? []) as ResourceTypeRow[]);
    setResources((res ?? []) as unknown as ResourceRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  async function handleAddService(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    const { data: created, error: err } = await supabase
      .from("services")
      .insert({
        business_id: activeBusinessId,
        name: svcName,
        category: svcCategory.trim() || null,
        duration_minutes: Number(svcDuration),
        price_cents: svcPrice ? Math.round(Number(svcPrice) * 100) : null,
        price_from: svcPrice ? svcPriceFrom : false,
        intake_fields: svcFields,
      })
      .select("id")
      .single();
    if (err || !created) {
      setError(err?.message ?? "Could not create the service.");
      return;
    }
    const staffIds = svcStaff ?? new Set(resources.filter((r) => r.active).map((r) => r.id));
    if (staffIds.size > 0) {
      const { error: ruleErr } = await supabase
        .from("service_resource_rules")
        .insert([...staffIds].map((resource_id) => ({ business_id: activeBusinessId, service_id: created.id, resource_id })));
      if (ruleErr) setError(ruleErr.message);
    }
    setSvcStaff(null);
    setSvcName("");
    setSvcCategory("");
    setSvcDuration("30");
    setSvcPrice("");
    setSvcPriceFrom(false);
    setSvcFields([]);
    await load();
  }

  /** Staff who can perform a service: named directly, or through their resource type. */
  function staffFor(serviceId: string): { direct: Set<string>; viaType: Set<string> } {
    const direct = new Set<string>();
    const viaType = new Set<string>();
    for (const rule of rules) {
      if (rule.service_id !== serviceId) continue;
      if (rule.resource_id) direct.add(rule.resource_id);
      else if (rule.resource_type_id) {
        for (const r of resources) if (r.resource_type_id === rule.resource_type_id) viaType.add(r.id);
      }
    }
    return { direct, viaType };
  }

  function staffSummary(serviceId: string): string {
    const { direct, viaType } = staffFor(serviceId);
    return resources
      .filter((r) => direct.has(r.id) || viaType.has(r.id))
      .map((r) => r.name)
      .join(", ");
  }

  function toggleIn(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function handleAddType(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !newTypeName) return;
    setError(null);
    const { data, error: err } = await supabase
      .from("resource_types")
      .insert({ business_id: activeBusinessId, name: newTypeName })
      .select()
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    setNewTypeName("");
    await load();
    if (data) setResTypeId(data.id);
  }

  /** A staff name that shares a word with the receptionist's name is refused by the database (migration 0023). */
  function staffNameError(message: string): string {
    const agent = message.match(/receptionist_name_conflict: (.+)/)?.[1];
    return agent
      ? `That name is too close to your receptionist's name ("${agent.trim()}"), so callers could confuse them. Use a different staff name, or rename the receptionist in Business settings.`
      : message;
  }

  /** The receptionist-name clash is shown in a pop-up so it cannot be missed; any other staff error stays at the top of the page. */
  function showStaffError(message: string) {
    const text = staffNameError(message);
    if (message.includes("receptionist_name_conflict")) setWarning(text);
    else setError(text);
  }

  async function handleAddResource(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    const { error: err } = await supabase.from("resources").insert({
      business_id: activeBusinessId,
      name: resName,
      resource_type_id: resTypeId || null,
    });
    if (err) {
      showStaffError(err.message);
      return;
    }
    setResName("");
    await load();
  }

  async function toggleResourceActive(row: ResourceRow) {
    await supabase.from("resources").update({ active: !row.active }).eq("id", row.id);
    await load();
  }

  function startEditingResource(row: ResourceRow) {
    setEditingResourceId(row.id);
    setEditResName(row.name);
    setEditResTypeId(row.resource_type_id ?? "");
  }

  async function saveEditingResource() {
    if (!editingResourceId) return;
    setError(null);
    const { error: err } = await supabase
      .from("resources")
      .update({ name: editResName, resource_type_id: editResTypeId || null })
      .eq("id", editingResourceId);
    if (err) {
      showStaffError(err.message);
      return;
    }
    setEditingResourceId(null);
    await load();
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
      <h1 className="rc-h1" style={{ marginBottom: -16 }}>Services & staff</h1>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}

      <section>
        <h2 className="rc-h2" style={{ marginBottom: 12 }}>Services</h2>
        {!readOnly && (
          <>
        <form onSubmit={handleAddService} className="rc-formbar" style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Name
            <input required value={svcName} onChange={(e) => setSvcName(e.target.value)} style={fieldStyle} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Category
            <input value={svcCategory} onChange={(e) => setSvcCategory(e.target.value)} list="rc-service-categories" maxLength={60} placeholder="optional" style={fieldStyle} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Duration (min)
            <input
              required
              type="number"
              min={1}
              value={svcDuration}
              onChange={(e) => setSvcDuration(e.target.value)}
              style={{ ...fieldStyle, width: 90 }}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Price ($)
            <input
              type="number"
              step="0.01"
              min={0}
              value={svcPrice}
              onChange={(e) => setSvcPrice(e.target.value)}
              style={{ ...fieldStyle, width: 90 }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--rc-text-muted)", paddingBottom: 8 }}>
            <input type="checkbox" checked={svcPriceFrom} disabled={!svcPrice} onChange={(e) => setSvcPriceFrom(e.target.checked)} />
            Starting price (“from”)
          </label>
          <button type="submit" style={{ padding: "8px 16px" }}>
            Add
          </button>
        </form>

        {resources.length > 0 && (
          <div style={{ marginBottom: 16, fontSize: 13 }}>
            <span style={{ color: "var(--rc-text-muted)" }}>Performed by (the new service can only be booked with these staff)</span>
            <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
              {resources
                .filter((r) => r.active)
                .map((r) => {
                  const selected = svcStaff ?? new Set(resources.filter((x) => x.active).map((x) => x.id));
                  return (
                    <label key={r.id}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => setSvcStaff(toggleIn(selected, r.id))}
                        style={{ marginRight: 6 }}
                      />
                      {r.name}
                    </label>
                  );
                })}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16, maxWidth: 560 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>
              Extra fields to collect for the new service (booking screen renders these -- see Bookings)
            </span>
            {industryTemplate && (
              <button
                type="button"
                onClick={() => setSvcFields(industryTemplate)}
                style={{ ...linkButtonStyle, fontSize: 12 }}
              >
                Use {industry} template
              </button>
            )}
          </div>
          <IntakeFieldListEditor fields={svcFields} onChange={setSvcFields} />
        </div>
          </>
        )}

        <datalist id="rc-service-categories">
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <div className="rc-table-scroll"><table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Name</th>
              <th style={{ padding: "10px 12px" }}>Category</th>
              <th style={{ padding: "10px 12px" }}>Duration</th>
              <th style={{ padding: "10px 12px" }}>Price</th>
              <th style={{ padding: "10px 12px" }}>Active</th>
              <th style={{ padding: "10px 12px" }}>Extra fields</th>
              <th style={{ padding: "10px 12px" }}>Performed by</th>
              <th style={{ padding: "10px 12px" }} />
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td data-label="Name" style={{ padding: "10px 12px" }}>{s.name}</td>
                <td data-label="Category" style={{ padding: "10px 12px" }}>{s.category ?? <span style={{ color: "var(--rc-text-faint)" }}>-</span>}</td>
                <td data-label="Duration" style={{ padding: "10px 12px" }}>{s.duration_minutes} min</td>
                <td data-label="Price" style={{ padding: "10px 12px" }}>{formatServicePrice(s.price_cents, s.price_from)}</td>
                <td data-label="Active" style={{ padding: "10px 12px" }}>{s.active ? "Yes" : <span style={{ color: "var(--rc-text-faint)" }}>No</span>}</td>
                <td data-label="Extra fields" style={{ padding: "10px 12px" }}>{(s.intake_fields ?? []).length > 0 ? `${s.intake_fields.length} field(s)` : "None"}</td>
                <td data-label="Performed by" style={{ padding: "10px 12px" }}>
                  {staffSummary(s.id) || <span style={{ color: "var(--rc-danger)" }}>No one (can&apos;t be booked)</span>}
                </td>
                <td className="rc-actions" style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  {!readOnly && (
                    <button onClick={() => setEditingService(s)} style={linkButtonStyle}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No services yet.
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      </section>

      <section>
        <h2 className="rc-h2" style={{ marginBottom: 12 }}>Staff / resources</h2>
        {!readOnly && (
          <>
        <form onSubmit={handleAddType} className="rc-formbar" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            New resource type (e.g. "barber", "chair")
            <input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} style={fieldStyle} />
          </label>
          <button type="submit" style={{ padding: "8px 16px" }}>
            Add type
          </button>
        </form>
        <form onSubmit={handleAddResource} className="rc-formbar" style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Name
            <input required value={resName} onChange={(e) => setResName(e.target.value)} style={fieldStyle} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
            Type
            <select value={resTypeId} onChange={(e) => setResTypeId(e.target.value)} style={fieldStyle}>
              <option value="">(none)</option>
              {resourceTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" style={{ padding: "8px 16px" }}>
            Add
          </button>
        </form>
          </>
        )}
        <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Name</th>
              <th style={{ padding: "10px 12px" }}>Type</th>
              <th style={{ padding: "10px 12px" }}>Active</th>
              <th style={{ padding: "10px 12px" }}>Login</th>
              <th style={{ padding: "10px 12px" }} />
            </tr>
          </thead>
          <tbody>
            {resources.map((r) =>
              editingResourceId === r.id ? (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)", background: "var(--rc-surface-sunken)" }}>
                  <td style={{ padding: "10px 12px" }}>
                    <input value={editResName} onChange={(e) => setEditResName(e.target.value)} style={fieldStyle} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <select value={editResTypeId} onChange={(e) => setEditResTypeId(e.target.value)} style={fieldStyle}>
                      <option value="">(none)</option>
                      {resourceTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: "10px 12px" }} />
                  <td style={{ padding: "10px 12px" }} />
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <button onClick={() => void saveEditingResource()} style={{ ...linkButtonStyle, marginRight: 12 }}>
                      Save
                    </button>
                    <button onClick={() => setEditingResourceId(null)} style={linkButtonStyle}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                  <td style={{ padding: "10px 12px" }}>{r.name}</td>
                  <td style={{ padding: "10px 12px" }}>{r.resource_types?.name ?? "-"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {readOnly ? (
                      r.active ? "Yes" : <span style={{ color: "var(--rc-text-faint)" }}>No</span>
                    ) : (
                      <button onClick={() => void toggleResourceActive(r)} style={linkButtonStyle}>
                        {r.active ? "Yes (deactivate)" : "No (activate)"}
                      </button>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {activeBusinessId && !readOnly && <StaffLoginCell businessId={activeBusinessId} resource={r} login={logins.get(r.id)} onChanged={() => void load()} />}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {!readOnly && (
                      <button onClick={() => startEditingResource(r)} style={linkButtonStyle}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              )
            )}
            {resources.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No staff/resources yet.
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      </section>

      <StaffHours reloadKey={resources.length} />
      {warning && (
        <Modal title="Staff name" onClose={() => setWarning(null)} width={440}>
          <p style={{ margin: "0 0 16px", color: "var(--rc-danger)" }} role="alert">{warning}</p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="button" variant="primary" onClick={() => setWarning(null)} autoFocus>OK</Button>
          </div>
        </Modal>
      )}
      {editingService && (
        <EditServiceModal
          service={editingService}
          resources={resources}
          directStaff={staffFor(editingService.id).direct}
          viaTypeStaff={staffFor(editingService.id).viaType}
          businessId={activeBusinessId!}
          industry={industry}
          industryTemplate={industryTemplate}
          categories={categories}
          onClose={() => setEditingService(null)}
          onSaved={() => {
            setEditingService(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Edit everything about one service in one place: details, active, extra intake fields and who
 *  performs it. Only the per-person staff rules are managed here; resource-type rules are left alone. */
function EditServiceModal({
  service,
  resources,
  directStaff,
  viaTypeStaff,
  businessId,
  industry,
  industryTemplate,
  categories,
  onClose,
  onSaved,
}: {
  service: ServiceRow;
  resources: ResourceRow[];
  directStaff: Set<string>;
  viaTypeStaff: Set<string>;
  businessId: string;
  industry: string | null;
  industryTemplate: IntakeFieldDef[] | undefined;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(service.name);
  const [category, setCategory] = useState(service.category ?? "");
  const [duration, setDuration] = useState(String(service.duration_minutes));
  const [price, setPrice] = useState(service.price_cents != null ? (service.price_cents / 100).toFixed(2) : "");
  const [priceFrom, setPriceFrom] = useState(service.price_from ?? false);
  const [active, setActive] = useState(service.active);
  const [createsJob, setCreatesJob] = useState(service.creates_job ?? false);
  const jobsEnabled = useJobsEnabled(businessId);
  const [fields, setFields] = useState<IntakeFieldDef[]>(service.intake_fields ?? []);
  const [staff, setStaff] = useState<Set<string>>(new Set(directStaff));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function toggleStaff(id: string) {
    setStaff((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const minutes = Number(duration);
    if (!name.trim()) {
      setError("A service needs a name.");
      return;
    }
    if (!Number.isInteger(minutes) || minutes < 1) {
      setError("Duration must be a whole number of minutes, 1 or more.");
      return;
    }
    const dollars = price.trim() === "" ? null : Number(price);
    if (dollars != null && (!Number.isFinite(dollars) || dollars < 0)) {
      setError("Price must be a number of zero or more, or left blank.");
      return;
    }

    setSaving(true);
    const { error: svcErr } = await supabase
      .from("services")
      .update({
        name: name.trim(),
        category: category.trim() || null,
        duration_minutes: minutes,
        price_cents: dollars == null ? null : Math.round(dollars * 100),
        price_from: dollars == null ? false : priceFrom,
        active,
        creates_job: createsJob,
        intake_fields: fields,
      })
      .eq("id", service.id);
    if (svcErr) {
      setSaving(false);
      setError(svcErr.message);
      return;
    }

    const staffChanged = staff.size !== directStaff.size || [...staff].some((id) => !directStaff.has(id));
    if (staffChanged) {
      const { error: delErr } = await supabase.from("service_resource_rules").delete().eq("service_id", service.id).not("resource_id", "is", null);
      if (delErr) {
        setSaving(false);
        setError(`Saved the details, but could not update staff: ${delErr.message}`);
        return;
      }
      if (staff.size > 0) {
        const { error: insErr } = await supabase
          .from("service_resource_rules")
          .insert([...staff].map((resource_id) => ({ business_id: businessId, service_id: service.id, resource_id })));
        if (insErr) {
          setSaving(false);
          setError(`Saved the details, but could not update staff: ${insErr.message}`);
          return;
        }
      }
    }
    setSaving(false);
    onSaved();
  }

  return (
    <Modal title={`Edit service`} onClose={onClose} width={640}>
      <form onSubmit={handleSave} noValidate style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Category (groups services on the booking page)">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} list="rc-service-categories-edit" maxLength={60} placeholder="none" />
          <datalist id="rc-service-categories-edit">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px" }}>
            <Field label="Duration (min)">
              <Input type="number" min={1} step={1} value={duration} onChange={(e) => setDuration(e.target.value)} />
            </Field>
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <Field label="Price ($)">
              <Input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="no price" />
            </Field>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 6, color: "var(--rc-text-muted)" }}>
              <input type="checkbox" checked={priceFrom && price.trim() !== ""} disabled={price.trim() === ""} onChange={(e) => setPriceFrom(e.target.checked)} />
              Starting price (“from $”)
            </label>
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active (can be booked)
        </label>
        {jobsEnabled === true && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, cursor: "pointer" }}>
            <input type="checkbox" checked={createsJob} onChange={(e) => setCreatesJob(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              Booking this makes a job (use it for the Inspection)
              <span style={{ display: "block", fontSize: 12, color: "var(--rc-text-muted)" }}>
                A job is added to Jobs at "Inspection booked", or the customer's open job moves there.
              </span>
            </span>
          </label>
        )}

        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--rc-text-muted)", marginBottom: 6 }}>Performed by</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {resources.map((r) => {
              const viaType = viaTypeStaff.has(r.id);
              return (
                <label key={r.id} style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={viaType || staff.has(r.id)} disabled={viaType} onChange={() => toggleStaff(r.id)} style={{ marginRight: 6 }} />
                  {r.name}
                  {viaType ? " (via type)" : ""}
                  {!r.active ? " (inactive)" : ""}
                </label>
              );
            })}
            {resources.length === 0 && <span style={{ fontSize: 13, color: "var(--rc-text-faint)" }}>Add staff first.</span>}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--rc-text-muted)" }}>Extra fields to collect when booking</span>
            {industryTemplate && (
              <button type="button" onClick={() => setFields(industryTemplate)} style={{ ...linkButtonStyle, fontSize: 12 }}>
                Use {industry} template
              </button>
            )}
          </div>
          <IntakeFieldListEditor fields={fields} onChange={setFields} />
        </div>

        {error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: 0 }}>{error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const fieldStyle: React.CSSProperties = { display: "block", marginTop: 4 };
const linkButtonStyle: React.CSSProperties = { background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 13 };
